import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import './staticContentPage.css'
import { Helmet } from 'react-helmet';

interface FAQItem {
  title: string;
  description: string;
}

interface FAQSection {
  [section: string]: FAQItem[];
}

const FAQPage = () => {
  const faqs: FAQSection = {
    "Getting Started": [
      {
        title: "What is Eco Eats?",
        description: "Eco Eats is a plant-based food marketplace where you can discover and order from the best local, eco-conscious restaurants and food makers. Learn more in our [About Us](/about) section.",
      },
      {
        title: "How do I place an order?",
        description: "It's simple! Browse menus from local eateries on our [search page](/search), add items to your cart, and place your order for collection.",
      },
      {
        title: "Is Eco Eats available in my city?",
        description: "We are launching in select South African cities. Check our homepage or follow us on social media for the latest updates on where we're live.",
      },
      {
        title: "Do I need an account to use Eco Eats?",
        description: "Yes, you'll need to [create a free account](/signup) to place orders and keep track of your purchases.",
      }
    ],
    "Orders & Pickups": [
      {
        title: "When can I pick up my food?",
        description: "Pickup times are set by the restaurant and are typically during their normal business hours. Please check the restaurant's page for their operating hours.",
      },
      {
        title: "What happens if I miss my pickup?",
        description: "If you don't collect your order by the close of business, it may be discarded, and a refund may not be possible as the food was prepared for you. Please refer to our [Refund Policy](/terms/refundPolicy) for details.",
      },
      {
        title: "Can I customize my order?",
        description: "Customization options depend on the merchant. Please check the item description for any available options or notes you can add for the restaurant.",
      },
      {
        title: "Can I change my order after I've paid?",
        description: "Once an order is accepted by the merchant, changes are not guaranteed as they may have already started preparing it. Please see our [Refund Policy](/terms/refundPolicy) for information on cancellations.",
      }
    ],
    "Refunds & Cancellations": [
      {
        title: "What is your refund policy?",
        description: "Please read our full [Refund Policy](/terms/refundPolicy) for detailed information on when refunds are issued.",
      },
      {
        title: "How do I request a refund?",
        description: "You can request a refund for eligible orders through your [order history page](/order/history).",
      },
      {
        title: "How long do refunds take?",
        description: "Approved refunds are typically processed within 7-10 business days, in accordance with [Yoco's refund policy](https://support.yoco.help/en/articles/109539-how-to-refund-a-payment).",
      }
    ],
    "Business Partners": [
      {
        title: "How can my business join Eco Eats?",
        description: `The best way to partner with us is to join our waiting list via the button on our homepage. Alternatively, you can [sign up](/signup) for an account and email us directly at [ecoeatsrsa@gmail.com](mailto:ecoeatsrsa@gmail.com) to get started.`,
      },
      {
        title: "What types of businesses can list on Eco Eats?",
        description: "We partner with restaurants, bakeries, cafes, and food producers that offer plant-based or eco-conscious food.",
      },
      {
        title: "How do payouts work for partners?",
        description: "You'll receive payouts for completed orders monthly via your linked business bank account. Weekly payouts can also be arranged during onboarding.",
      }
    ],
    "Technical Support": [
      {
        title: "I've placed an order but can't find the confirmation email. What should I do?",
        description: "Sometimes confirmation emails can get lost in spam or promotions folders. Please check there first. You can always find a record of all your confirmed orders on your [order history page](/order/history). If you've checked and still can't find it, please contact us at [ecoeatsrsa@gmail.com](mailto:ecoeatsrsa@gmail.com).",
      },
      {
        title: "The website isn't loading properly. How do I fix it?",
        description: "Please try refreshing the page. If the issue continues, don't hesitate to contact our support team at [ecoeatsrsa@gmail.com](mailto:ecoeatsrsa@gmail.com).",
      },
      {
        title: "How do I update my personal information?",
        description: `Log into your account and open the side menu. Under the "Manage" section, you'll find the [Profile](/profile) link where you can edit your details.`,
      }
    ],
    "General Info": [
      {
        title: "Is Eco Eats safe to use?",
        description: "Absolutely. We partner with verified businesses, and all payments are processed securely by our trusted payment partner, [Yoco](https://www.yoco.com/za/).",
      },
      {
        title: "Why is Eco Eats focused on plant-based food?",
        description: "We believe that a plant-rich diet is a powerful way to create a more sustainable and compassionate world. Our goal is to make conscious eating delicious, accessible, and community-driven.",
      },
      {
        title: "Does Eco Eats help the environment?",
        description: "Yes! By promoting plant-based eating and supporting local businesses, every order contributes to a smaller environmental footprint and fosters a more sustainable local food economy.",
      }
    ]
  };


  return (
    <div className="pat">
      <Helmet>
        <title>Eco Eats FAQ | Help & Support for Our Plant-Based Marketplace</title>
        <meta
          name="description"
          content="Find answers to your questions about Eco Eats - South Africa's plant-based food marketplace. Learn how to order, support local, and eat consciously."
        />
        <meta
          name="image"
          content="./src_assets/drawer_logo.svg"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />


        {/* */}
        <meta property="og:title" content="Eco Eats FAQ | Help & Support for Our Plant-Based Marketplace" />
        <meta property="og:description" content="Find answers to your questions about Eco Eats - South Africa's plant-based food marketplace. Learn how to order, support local, and eat consciously." />
        <meta property="og:image" content="./src_assets/drawer_logo.svg" />

        {/* */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eco Eats FAQ | Help & Support for Our Plant-Based Marketplace" />
        <meta name="twitter:description" content="Find answers to your questions about Eco Eats - South Africa's plant-based food marketplace. Learn how to order, support local, and eat consciously." />
        <meta name="twitter:image" content="./src_assets/drawer_logo.svg" />
      </Helmet>
      <Box p={4} >
        <Typography variant="h4" gutterBottom>
          Frequently Asked Questions
        </Typography>

        {Object.entries(faqs).map(([section, questions]) => (
          <Box key={section} mb={5}>
            <Typography variant="h5" gutterBottom mt={4}>
              {section}
            </Typography>
            {questions.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontFamily={{
                    fontFamily: "Lato"
                  }}>{faq.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>

                  <div style={{ fontFamily: "Lato, sans-serif" }}>
                    <ReactMarkdown
                      children={faq.description}
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeSanitize]}
                    /></div>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}
      </Box>
    </div>
  );
};

export default FAQPage;
